import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Tab,
  Tabs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  FormControl,
  Select,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import addServiceToAgent from '../services/addServicesToAgent';
import { SERVICESTYPE } from '../../../../../services/config/CONST';
import { Service } from '../../../../../types/Service.types';
import AlertMessage from '../../../../../components/AlertMessage/AlertMessage';
import { MARKERS } from '../../../../../features/usage-tracking/usageKeys';
import { useDialogUsage } from '../../../../../features/usage-tracking/hooks/useDialogUsage';

function TabPanel(props: { children: React.ReactNode; index: any; value: any }) {
  const { children, value, index, ...other } = props;
  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...other}
    >
      {value === index && <Box p={0}>{children}</Box>}
    </Typography>
  );
}

type Props = {
  allServices: Service[];
  agent_id: string | undefined;
  refreshServicesById: () => void;
};

function DialogAddServiceToAgent({ allServices, agent_id, refreshServicesById }: Props) {
  const [open, setOpen] = useState(false);
  const [servicesList, setServicesList] = useState<Service[]>([]);
  const [currentService, setCurrentService] = useState<Service | undefined>();
  const [singleService, setSingleService] = useState<Service[]>([]);
  const [monthlyService, setMonthlyService] = useState<Service[]>([]);
  const [yearlyService, setYearlyService] = useState<Service[]>([]);
  const [value, setValue] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  // RITA-1504 (Entwurf E): genau ein Ereignis je Öffnung — "Ok" bestätigt, jeder andere Weg
  // heraus bricht ab. Escape schließt hier nie (disableEscapeKeyDown), der Hintergrund schon.
  const dialogUsage = useDialogUsage('berater.leistung.hinzufuegen');

  useEffect(() => {
    const singleArray: Service[] = [];
    const monthlyArray: Service[] = [];
    const yearlyArray: Service[] = [];

    allServices.map((item) => {
      if (item.type === SERVICESTYPE.MonthlyService) {
        monthlyArray.push(item);
      } else if (item.type === SERVICESTYPE.SingleService) {
        singleArray.push(item);
      } else if (item.type === SERVICESTYPE.YearlyService) {
        yearlyArray.push(item);
      }
      return {};
    });

    setSingleService(singleArray);
    setMonthlyService(monthlyArray);
    setYearlyService(yearlyArray);
    setServicesList(allServices);
  }, [allServices]);

  // CONTRACT: R-1 — currentService WIRD GESETZT wenn Dialog öffnet (open=true) UND nach jedem
  // Tab-Wechsel (value). Immer auf die erste Option des aktiven Tabs. Wird GELOEST wenn Dialog
  // schließt (open=false → early return). onChange bleibt für manuelle Auswahl, ist aber kein
  // Initializer — native <select> feuert onChange NICHT für die vorausgewählte Option (HOTSPOTS).
  useEffect(() => {
    if (!open) return;
    let activeList: Service[];
    if (value === 0) {
      activeList = singleService;
    } else if (value === 1) {
      activeList = monthlyService;
    } else {
      activeList = yearlyService;
    }
    setCurrentService(activeList[0]);
  }, [open, value, singleService, monthlyService, yearlyService]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChange1 = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedService = servicesList.find((item) => item.id === parseInt(event.target.value, 10));
    setCurrentService(selectedService);
  };

  const handleAddServiceToAgent = async () => {
    // CONTRACT: R-2 — Ist kein gültiger Service ermittelbar, wird keine Mutation ausgelöst
    // und eine sichtbare Rückmeldung gezeigt. Dialog bleibt offen.
    if (!currentService) {
      setErrorMessage('Keine Leistung ausgewählt. Bitte wählen Sie eine Leistung aus.');
      return;
    }
    // Erst hier steht die Bestätigung fest: ohne wählbare Leistung bleibt der Dialog offen und
    // die Entscheidung aus.
    dialogUsage.confirmed();
    try {
      const response = await addServiceToAgent(currentService, agent_id);
      if (response?.data?.addServiceToAgent) {
        // CONTRACT: errorMessage MUST be cleared on success — without this, a previous failure
        // survives the success path and the AlertMessage (rendered outside the Dialog) stays red.
        setErrorMessage('');
        setOpen(false);
        refreshServicesById();
      } else {
        setErrorMessage('Die Leistung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
      }
    } catch {
      setErrorMessage('Die Leistung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
    }
  };

  const handleClickOpen = () => {
    dialogUsage.reset();
    setOpen(true);
  };

  const handleClose = () => {
    dialogUsage.cancelled();
    setOpen(false);
    setErrorMessage('');
  };

  return (
    <div>
      <Button
        color="secondary"
        variant="outlined"
        title="neue Leistung hinzufügen"
        onClick={handleClickOpen}
        data-usage={MARKERS.beraterLeistungNeu}
      >
        +
      </Button>
      <AlertMessage
        typeMessage="error"
        alertMessage={errorMessage}
        isAlertMessage={!!errorMessage}
        handleChangeIsAlertMessage={() => setErrorMessage('')}
      />
      <Dialog disableEscapeKeyDown open={open} onClose={handleClose}>
        <DialogTitle>neue Leistung hinzufügen</DialogTitle>
        <DialogContent>
          <Tabs value={value} onChange={handleChange1} indicatorColor="primary" textColor="primary">
            <Tab label="Einmalig" />
            <Tab label="Monatlich" />
            <Tab label="Jährlich" />
          </Tabs>
          <TabPanel value={value} index={0}>
            <Box display="flex" flexWrap="wrap">
              <FormControl
                variant="filled"
                sx={{
                  margin: 1,
                  minWidth: '90%',
                }}
              >
                <InputLabel htmlFor="filled-age-native-simple">wählen Sie eine Leistung</InputLabel>
                <Select
                  native
                  value={currentService?.id?.toString() || singleService[0]?.id?.toString() || ''}
                  onChange={handleChange}
                  inputProps={{
                    name: 'age',
                    id: 'filled-age-native-simple',
                  }}
                >
                  {singleService.map((service) => (
                    <option key={service.id} value={service.id.toString()}>
                      {service.title}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </TabPanel>
          <TabPanel value={value} index={1}>
            <FormControl
              variant="filled"
              sx={{
                margin: 1,
                minWidth: '90%',
              }}
            >
              <InputLabel htmlFor="filled-age-native-simple">wählen Sie eine Leistung</InputLabel>
              <Select
                native
                value={currentService?.id?.toString() || monthlyService[0]?.id?.toString() || ''}
                onChange={handleChange}
                inputProps={{
                  name: 'age',
                  id: 'filled-age-native-simple',
                }}
              >
                {monthlyService.map((service) => (
                  <option key={service.id} value={service.id.toString()}>
                    {service.title}
                  </option>
                ))}
              </Select>
            </FormControl>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <Box display="flex" flexWrap="wrap">
              <FormControl
                variant="filled"
                sx={{
                  margin: 1,
                  minWidth: '90%',
                }}
              >
                <InputLabel htmlFor="filled-age-native-simple">wählen Sie eine Leistung</InputLabel>
                <Select
                  native
                  value={currentService?.id?.toString() || yearlyService[0]?.id?.toString() || ''}
                  onChange={handleChange}
                  inputProps={{
                    name: 'age',
                    id: 'filled-age-native-simple',
                  }}
                >
                  {yearlyService.map((service) => (
                    <option key={service.id} value={service.id.toString()}>
                      {service.title}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddServiceToAgent} color="primary">
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DialogAddServiceToAgent;
